import React, { useState, useEffect, Fragment } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon as FAIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faPaperPlane, faExclamationTriangle, faUndo } from '@fortawesome/free-solid-svg-icons';
import StudentContent from './Student/StudentContent';
import TeacherContent from './Teacher/TeacherContent';
import Helper, { Button} from './Helper/Helper';
import Form from './Form/Form';
import Photo from './Student/Photo';
import { imgix } from '../../lib/env';
import loader from '../../images/loader.gif';

const { imgix_domain, s3_domain_to_replace, imgix_parameters_student } = imgix;

const Slider = styled.div`
  display: block;
  overflow-x: hidden;
  width: 30rem;
  height: 100vh;
  box-shadow: var(--elevation-1);
  top: 0;
  right: -100px;
  position: absolute;
  z-index: 5;
  white-space: nowrap;
  background-color: white;

  @-webkit-keyframes slide-left {
    0% {
      -webkit-transform: translateX(0);
      transform: translateX(0);
    }
    100% {
      -webkit-transform: translateX(-100px);
      transform: translateX(-100px);
    }
  }
  @keyframes slide-left {
    0% {
      -webkit-transform: translateX(0);
      transform: translateX(0);
    }
    100% {
      -webkit-transform: translateX(-100px);
      transform: translateX(-100px);
    }
  }

  -webkit-animation: slide-left 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  animation: slide-left 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;

  ${props => !props.show
    && css`
      display: none;
      width: 0;
      transition: 250ms ease-in;
    `};
`;

const EmailPhotos = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Container = styled.div`
  display: ${props => (props.flex ? 'flex' : 'grid')};
  grid-auto-rows: min-content;
  grid-gap: 1.5rem;
  padding: 1rem 2rem;
`;

const IconHolder = styled.div`
  svg {
    height: 100%;
    width: 100%;
    cursor: pointer;
  }
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusText = styled.p`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;
  align-items: center;
  color: ${props => (props.active ? 'var(--color-green)' : 'var(--font-color-faded)')};
`;

const Heading = styled.h1`
  font-size: 2.5rem;
`;

const HeadingWrapper = styled.div`
  white-space: initial;
`;

const checkEmail = (email) => {
  const expression = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return expression.test(String(email).toLowerCase());
};

const Sidebar = ({
  sidebar,
  toggle,
  type,
  sendEmail,
  sendPhotosViaEmail,
  clearEmailPhotos,
  conventionEmail,
  emailState,
  updateStudentEmail,
  updateTeacherEmail,
  getClassroomStudents,
  getStudentsPhotos,
  teacher,
  student,
  customEmail,
  trash,
  emailPhotos,
  trashedPhotos,
  handleDelete,
  unassignStudent,
  deletePhotoFromEmailPhotos,
  restorePhotoFromTrash,
  restoreAllPhotosFromTrash,
  emptyTrash,
  isLoadingTrashSidebar,
  isLoadingEmailSidebar,
}) => {
  const { errorMessages, ...args } = emailState;
  const { properties } = sidebar;
  const {
    first_name, last_name, className, id, email, teacher_id,
  } = properties;

  const [emailValue, changeEmailState] = useState('');
  const [isFormValidated, validateForm] = useState(false);
  const [changes, toggleChangesStatus] = useState(false);
  const [touched, touchInput] = useState(false);
  const [showErrors, changeShowErrors] = useState(false);

  const updateEmail = () => {
    if (type === 'student') updateStudentEmail(id, emailValue);
    if (type === 'teacher') updateTeacherEmail(teacher_id, emailValue, id);
  };

  useEffect(() => {
    if (errorMessages.length > 0) {
      changeShowErrors(true);
    }
  }, [errorMessages]);

  useEffect(() => {
    let timeout = '';
    if (emailValue !== '' && isFormValidated) {
      timeout = setTimeout(() => {
        updateEmail();
      }, 1000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [emailValue]);

  useEffect(() => {
    if (emailState.updated) {
      toggleChangesStatus(true);
      touchInput(false);
    }
  }, [emailState]);

  useEffect(() => {
    changeEmailState(email);
  }, [email]);

  useEffect(() => {
    toggleChangesStatus(false);
  }, []);

  const changeEmailValue = (value) => {
    changeShowErrors(false);
    touchInput(true);
    toggleChangesStatus(false);
    const emailCheckValue = checkEmail(value);

    if (emailCheckValue !== isFormValidated) validateForm(emailCheckValue);
    changeEmailState(value);
  };

  const globalShow = student.show || teacher.show || customEmail.show || trash.show;

  const mergedEmailState = {
    ...args,
    value: emailValue,
  };

  const isValidationError = touched && emailValue.length > 5 && !isFormValidated;
  return (
    <Slider show={globalShow}>
      <Container>
        <StatusBar>
          <StatusText active={changes}>
            <FAIcon icon={faCheck} />
            Änderungen gespeichert
          </StatusText>
          <IconHolder onClick={() => toggle(type, false)}>
            <FAIcon icon={faTimes} />
          </IconHolder>
        </StatusBar>
        <HeadingWrapper>
          <Heading>{`${first_name} ${last_name}`}</Heading>
        </HeadingWrapper>
        {customEmail.show && ( isLoadingEmailSidebar ? <img src={loader} alt=""  /> : (
          <Fragment>
            <Form
              changeEmailValue={changeEmailValue}
              emailState={mergedEmailState}
              errorMessages={errorMessages}
              showErrors={showErrors}
              isValidationError={isValidationError}
            />
            <EmailPhotos>
              {emailPhotos.map((photo, index) => {
                return (
                  <Photo
                    fixed
                    key={index}
                    src={photo.attributes.url.replace(s3_domain_to_replace, imgix_domain) + imgix_parameters_student}
                    handleDelete={() => deletePhotoFromEmailPhotos(photo.id)}
                  />
                )
              })}
            </EmailPhotos>
          </Fragment>)
        )}
        {trash.show && (
          <Fragment>
            { isLoadingTrashSidebar ? <img src={loader} alt=""  /> : (
              <EmailPhotos>
                {trashedPhotos.map((photo, index) => (
                  <Photo
                    fixed
                    alt={`${index}_trashed`}
                    key={photo.id}
                    src={photo.attributes.url.replace(s3_domain_to_replace, imgix_domain) + imgix_parameters_student}
                    handleDelete={() => restorePhotoFromTrash(photo.id)} />
                ))}
              </EmailPhotos>
            ) }
          </Fragment>
        )}
        {teacher.show && (
          <Fragment>
            <Form
              changeEmailValue={changeEmailValue}
              emailState={mergedEmailState}
              errorMessages={errorMessages}
              showErrors={showErrors}
              isValidationError={isValidationError}
              teacher
            />
            <TeacherContent
              className={className}
              teacher={teacher}
              getClassroomStudents={getClassroomStudents}
              classroomId={id}
              unassignStudent={unassignStudent}
            />
          </Fragment>
        )}
        {student.show && (
          <Fragment>
            <Form
              changeEmailValue={changeEmailValue}
              emailState={mergedEmailState}
              isValidationError={isValidationError}
              errorMessages={errorMessages}
              showErrors={showErrors}
              student
            />
            <StudentContent
              student={student}
              getStudentsPhotos={getStudentsPhotos}
              handleDelete={handleDelete}
              studentId={id}
            />
          </Fragment>
        )}
      </Container>

      { (student.show || teacher.show) && <Helper id={id} sendEmail={sendEmail} emailState={conventionEmail} />} 

      {customEmail.show && (
        <Container flex>
          <Button type="button" onClick={() => sendPhotosViaEmail(emailValue)}>
            <FAIcon icon={faPaperPlane} />
            E-Mail erneut senden
          </Button>
          <Button invert type="button" onClick={() => clearEmailPhotos()}>
            Cancel
          </Button>
        </Container>
      ) }
      {trash.show && (
        <Container flex>
          <Button type="button" onClick={() => emptyTrash()}>
            <FAIcon icon={faExclamationTriangle} size='lg' />
            Empty Trash
          </Button>
          <Button invert type="button" onClick={() => restoreAllPhotosFromTrash()}>
            <FAIcon icon={faUndo} />
            Restore All
          </Button>
        </Container>
      )}
    </Slider>
  );
};

export default Sidebar;



// WEBPACK FOOTER //
// ./src/components/Sidebar/Sidebar.jsx